import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

export interface KnowledgeBaseDocument {
  id: string;
  title: string;
  content: string;
  category: string;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string;
}

export function useKnowledgeBase() {
  const [documents, setDocuments] = useState<KnowledgeBaseDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const getDocsFn = httpsCallable(functions, 'getKnowledgeBaseDocs');
      const result = await getDocsFn();
      const data = result.data as { success: boolean; documents: KnowledgeBaseDocument[] };

      if (data.success) {
        setDocuments(data.documents);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  return {
    documents,
    loading,
    error,
    refetch: fetchDocuments
  };
}

export function useKnowledgeBaseDocument(documentId: string | undefined) {
  const [document, setDocument] = useState<KnowledgeBaseDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      setLoading(false);
      return;
    }

    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        const getDocFn = httpsCallable(functions, 'getKnowledgeBaseDoc');
        const result = await getDocFn({ documentId });
        const data = result.data as { success: boolean; document: KnowledgeBaseDocument };

        if (data.success) {
          setDocument(data.document);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch document');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId]);

  return {
    document,
    loading,
    error
  };
}

export async function createDocument(
  title: string,
  content: string,
  category: string
): Promise<{ success: boolean; documentId?: string; error?: string }> {
  try {
    const createFn = httpsCallable(functions, 'createKnowledgeBaseDoc');
    const result = await createFn({ title, content, category });
    const data = result.data as { success: boolean; documentId: string };

    return { success: true, documentId: data.documentId };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to create document' };
  }
}

export async function updateDocument(
  documentId: string,
  title: string,
  content: string,
  category: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const updateFn = httpsCallable(functions, 'updateKnowledgeBaseDoc');
    await updateFn({ documentId, title, content, category });

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to update document' };
  }
}

export async function deleteDocument(
  documentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const deleteFn = httpsCallable(functions, 'deleteKnowledgeBaseDoc');
    await deleteFn({ documentId });

    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to delete document' };
  }
}
